"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { trackPageView, identifyFromLink, withWhatsAppVisitTag } from "../lib/engageXClient";

const NAME = "lp_vid";
const FIRST_TOUCH = "lp_first_touch";
const TWO_YEARS = 63072000;

const readCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)lp_vid=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : "";
};

// The server (middleware) issues lp_vid. This only fills the gap when it is
// missing, e.g. a page served from cache; rewriting an existing cookie from
// script would put it back under Safari's 7-day limit.
const ensureVid = () => {
  let vid = readCookie();
  const fromServer = Boolean(vid);
  try {
    vid ||= localStorage.getItem(NAME) || "";
  } catch {}
  vid ||= crypto.randomUUID?.() ||
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

  if (!fromServer) {
    document.cookie = `${NAME}=${encodeURIComponent(vid)};path=/;max-age=${TWO_YEARS};SameSite=Lax;Secure`;
  }
  try {
    localStorage.setItem(NAME, vid);
  } catch {}
  return vid;
};

// Recorded once, on the first visit, and never overwritten. CaptureClickId
// keeps the latest UTMs for the CRM payload; this keeps where they came from.
const readFirstTouch = () => {
  try {
    const stored = localStorage.getItem(FIRST_TOUCH);
    if (stored) return JSON.parse(stored);
  } catch {}

  const params = new URLSearchParams(window.location.search);
  let referrer = "";
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : "";
    if (host && host !== window.location.hostname) referrer = host;
  } catch {}

  const touch = {
    source:
      params.get("utm_source") ||
      (params.get("gclid") ? "google_ads" : "") ||
      (params.get("fbclid") ? "facebook" : "") ||
      referrer ||
      "direct",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || "",
    at: new Date().toISOString(),
  };

  try {
    localStorage.setItem(FIRST_TOUCH, JSON.stringify(touch));
  } catch {}
  return touch;
};

// Rybbit treats a null trait as "delete this key", and its query fails when
// exactly one key is null — so a single null dropped the whole profile write.
// Omit empty traits instead of sending null.
const compact = (traits) =>
  Object.fromEntries(
    Object.entries(traits).filter(([, v]) => v !== null && v !== undefined && v !== "")
  );

export default function EngageXIdentity() {
  const pathname = usePathname();

  // Tell EngageX about each page a known lead looks at. It decides whether the
  // visit should start a journey ("came back, did nothing for two hours").
  // Arrived from one of our emails or WhatsApps: the link carries a signed
  // ?lx= that tells EngageX whose browser this is. Report it once, then take
  // it out of the address bar so it is not bookmarked or shared.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lx = params.get("lx");
    if (!lx) return;
    identifyFromLink(lx);
    params.delete("lx");
    const rest = params.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`);
  }, []);

  useEffect(() => {
    if (pathname) trackPageView(pathname);
  }, [pathname]);

  // Any plain WhatsApp link (footer, CMS content, future buttons) gets the
  // invisible visit tag at the moment it is clicked.
  useEffect(() => {
    const onClick = (event) => {
      const link = event.target?.closest?.(
        'a[href*="wa.me/"], a[href*="api.whatsapp.com/send"], a[href*="web.whatsapp.com/send"]'
      );
      if (link) link.href = withWhatsAppVisitTag(link.href);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    const vid = ensureVid();
    const firstTouch = readFirstTouch();

    window.LP = window.LP || {};
    window.LP.vid = vid;
    window.LP.firstTouch = firstTouch;

    // The analytics script loads afterInteractive, usually after this effect
    // runs. Wait for it, identify once, then stop — give up after ~20s.
    let attempts = 0;
    const tryIdentify = () => {
      if (!window.rybbit?.identify) return ++attempts >= 40;
      window.rybbit.identify(
        vid,
        compact({
          first_touch_source: firstTouch.source,
          first_touch_medium: firstTouch.medium,
          first_touch_campaign: firstTouch.campaign,
          first_seen: firstTouch.at,
        })
      );
      return true;
    };

    if (tryIdentify()) return;
    const interval = window.setInterval(() => {
      if (tryIdentify()) window.clearInterval(interval);
    }, 500);
    return () => window.clearInterval(interval);
  }, []);

  return null;
}
